import { useState } from 'react';
import Sidebar from '../components/Sidebar';

interface Staff {
  id: number;
  name: string;
  role: string;
  contact: string;
  email: string;
  isExternal: boolean;
  scheduleCount: number;
}

export default function StudioStaffPage() {
  const [currentDate, setCurrentDate] = useState(new Date(2024, 0, 1));
  const [selectedStaff, setSelectedStaff] = useState<number | null>(1);

  const staff: Staff[] = [
    {
      id: 1,
      name: '박작가',
      role: '스냅 작가',
      contact: '010-1111-2222',
      email: 'park@studio.com',
      isExternal: false,
      scheduleCount: 8
    },
    {
      id: 2,
      name: '최작가',
      role: '스냅 작가',
      contact: '010-3333-4444',
      email: 'choi@studio.com',
      isExternal: false,
      scheduleCount: 6
    },
    {
      id: 3,
      name: '정작가',
      role: '영상 작가',
      contact: '010-5555-6666',
      email: 'jung@studio.com',
      isExternal: false,
      scheduleCount: 5
    },
    {
      id: 4,
      name: '김보조',
      role: '보조 작가',
      contact: '010-7777-8888',
      email: 'kim@studio.com',
      isExternal: false,
      scheduleCount: 10
    },
    {
      id: 5,
      name: '이외주',
      role: '외주 작가',
      contact: '010-9999-0000',
      email: 'lee@external.com',
      isExternal: true,
      scheduleCount: 3
    }
  ];

  const schedules = [
    { id: 1, date: '2024-01-25', time: '10:00', customer: '김지은', type: '리허설', staffId: 1 },
    { id: 2, date: '2024-01-25', time: '14:00', customer: '이민수', type: '본식스냅', staffId: 2 },
    { id: 3, date: '2024-01-26', time: '11:00', customer: '박영희', type: '본식영상', staffId: 3 },
    { id: 4, date: '2024-01-28', time: '09:00', customer: '최수진', type: '리허설', staffId: 1 },
    { id: 5, date: '2024-01-28', time: '15:00', customer: '정민호', type: '본식스냅', staffId: 1 }
  ];

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (year: number, month: number, day: number) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const getSchedulesForStaffAndDate = (staffId: number, dateString: string) => {
    return schedules.filter(schedule => 
      schedule.staffId === staffId && schedule.date === dateString
    );
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const renderCalendar = () => {
    if (!selectedStaff) return null;

    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

    const weekHeader = weekDays.map(day => (
      <div key={day} className="p-2 text-center font-medium text-gray-600 bg-gray-50">
        {day}
      </div>
    ));

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2 h-24 border border-gray-200"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = formatDate(currentDate.getFullYear(), currentDate.getMonth(), day);
      const daySchedules = getSchedulesForStaffAndDate(selectedStaff, dateString);
      const isToday = dateString === new Date().toISOString().split('T')[0];

      days.push(
        <div
          key={day}
          className={`p-2 h-24 border border-gray-200 ${
            isToday ? 'bg-blue-50' : ''
          } ${daySchedules.length > 0 ? 'bg-yellow-50' : ''}`}
        >
          <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-gray-900'}`}>
            {day}
          </div>
          <div className="space-y-1">
            {daySchedules.map((schedule) => (
              <div
                key={schedule.id}
                className="text-xs p-1 rounded truncate bg-blue-100 text-blue-800"
              >
                {schedule.time} {schedule.customer}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="grid grid-cols-7">
          {weekHeader}
          {days}
        </div>
      </div>
    );
  };

  const selectedStaffData = staff.find(s => s.id === selectedStaff);

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-800">작가·스태프 배정 관리</h1>
            <p className="text-sm text-gray-600 mt-1">작가별 스케줄을 확인하고 배정을 관리하세요</p>
          </div>

          <div className="grid grid-cols-12 gap-6">
            {/* 작가 목록 */}
            <div className="col-span-3">
              <div className="bg-white rounded-lg shadow-sm p-4">
                <h2 className="text-lg font-bold text-gray-800 mb-4">작가 목록</h2>
                <div className="space-y-2">
                  {staff.map((person) => (
                    <div
                      key={person.id}
                      onClick={() => setSelectedStaff(person.id)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedStaff === person.id
                          ? 'bg-blue-50 border-2 border-blue-500'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <i className="ri-user-line text-blue-600"></i>
                          </div>
                          <div>
                            <div className="font-medium text-gray-800">{person.name}</div>
                            <div className="text-xs text-gray-500">{person.role}</div>
                          </div>
                        </div>
                        {person.isExternal && (
                          <span className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded-full">
                            외주
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-600">
                        이번 달 일정: {person.scheduleCount}건
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 작가 캘린더 */}
            <div className="col-span-9">
              {selectedStaffData && (
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                      <i className="ri-user-line text-2xl text-blue-600"></i>
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-800">{selectedStaffData.name}</h2>
                      <p className="text-sm text-gray-600">{selectedStaffData.role}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-600 mb-1">연락처</div>
                      <div className="text-sm font-medium text-gray-800">{selectedStaffData.contact}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-600 mb-1">이메일</div>
                      <div className="text-sm font-medium text-gray-800">{selectedStaffData.email}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-600 mb-1">이번 달 일정</div>
                      <div className="text-sm font-medium text-blue-600">{selectedStaffData.scheduleCount}건</div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-4">
                  <button
                    onClick={goToPreviousMonth}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                  >
                    <i className="ri-arrow-left-line text-gray-600"></i>
                  </button>
                  <h2 className="text-xl font-semibold text-gray-800">
                    {currentDate.getFullYear()}년 {currentDate.getMonth() + 1}월
                  </h2>
                  <button
                    onClick={goToNextMonth}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                  >
                    <i className="ri-arrow-right-line text-gray-600"></i>
                  </button>
                </div>
              </div>

              {renderCalendar()}

              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-blue-600 text-xl"></i>
                  <div>
                    <p className="text-sm font-medium text-blue-900 mb-1">일정 충돌 방지</p>
                    <p className="text-sm text-blue-700">같은 시간대에 중복 배정 시 자동으로 경고가 표시됩니다.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}